import type { JSX } from 'preact';
import { useState, useEffect } from 'preact/hooks';
import styles from './LiveToast.module.css';

export interface LiveToastAction {
  label: string;
  onClick: () => void;
}

interface LiveToastProps {
  message: string;
  duration?: number;
  onDismiss: () => void;
  /**
   * Turns the toast into something the reader can act on.
   *
   * An actionable toast does NOT auto-dismiss. Announcing that the thing someone
   * fed is finally ready and then retracting the only route to it after three
   * seconds is the same dead end this work exists to close — just smaller and
   * harder to notice. The timer is for ambient news ("New take just dropped");
   * a payoff waits to be dealt with.
   */
  action?: LiveToastAction;
  /**
   * Position in a stack of simultaneously-visible toasts (0 = topmost).
   *
   * Toasts are fixed to the same coordinates, so two of them at once land on
   * top of each other and the one underneath loses both its message and its
   * button. That was survivable while every toast expired on its own; now that
   * a toast carrying a route waits indefinitely, an occluded one stays occluded.
   * Callers that can show more than one at a time number them.
   */
  stackIndex?: number;
}

export function LiveToast({
  message,
  duration = 3000,
  onDismiss,
  action,
  stackIndex = 0,
}: LiveToastProps) {
  const [exiting, setExiting] = useState(false);

  const dismiss = () => {
    setExiting(true);
    setTimeout(onDismiss, 300);
  };

  useEffect(() => {
    if (action) return;
    const timer = setTimeout(() => {
      setExiting(true);
      setTimeout(onDismiss, 300);
    }, duration);

    return () => clearTimeout(timer);
  }, [duration, onDismiss, action]);

  return (
    <div
      class={`${styles.toast} ${exiting ? styles.exiting : ''}`}
      style={{ '--st-toast-stack-index': stackIndex } as JSX.CSSProperties}
      role="status"
      aria-live="polite"
    >
      <span class={styles.dot} />
      <span class={styles.message}>{message}</span>
      {action && (
        <>
          <button type="button" class={styles.action} onClick={action.onClick}>
            {action.label}
          </button>
          <button type="button" class={styles.close} onClick={dismiss} aria-label="Dismiss">
            &times;
          </button>
        </>
      )}
    </div>
  );
}
